/**
 * PAYMENT TYPES — SUNMI additions (owner-approved mockup board 11).
 * Spec: docs/agenticpos-rubytill/sunmi-card-setup.md §3, §5.
 *
 * Builds the `cardSetup` view the shared RegisterPaymentTypes card draws ONLY
 * for a register whose model has a `cardSetupPolicy` and which has a stored
 * `cardSetup`: the readers line, the debit routing + PIN lock, and the tip
 * location. Nothing here changes a stored payment-types field.
 */
import React from 'react';
import { MapPin } from 'lucide-react';
import { Pill, SubRow, TextAction } from '../payment-types/ui';
import type { CardSetupView } from '../payment-types/RegisterPaymentTypes';
import { Segmented } from './ReaderPicker';
import {
  type CardSetupPolicy, type ResolvedReader, type TipLocation,
  capabilitySummary, kindLabel, paymentLocks, tipLocationOptions,
} from './cardSetupModel';

const readerName = (r: ResolvedReader) => kindLabel(r.kind);

/** "Primary · Android Tap to Pay · credit" */
export const readerLineText = (r: ResolvedReader) =>
  `${r.role === 'primary' ? 'Primary' : 'Backup'} · ${readerName(r)} · ${capabilitySummary(r.capabilities)}`;

export function buildCardSetupView(args: {
  policy: CardSetupPolicy;
  readers: ResolvedReader[];
  tipLocation: TipLocation;
  onTipLocation: (t: TipLocation) => void;
  onOpenTerminalTab?: () => void;
  disabled?: boolean;
  /** No card setup saved yet: the tip choice is kept and saved with the readers. */
  pending?: boolean;
}): CardSetupView {
  const { policy, readers, tipLocation, onTipLocation, onOpenTerminalTab, disabled, pending } = args;
  const locks = paymentLocks(readers);
  const opts = tipLocationOptions(policy, readers);
  const link = onOpenTerminalTab ? (
    <TextAction testId="pt-open-terminal-tab" onClick={onOpenTerminalTab} disabled={disabled} title="Change the readers on the Terminal (EMV/SoftPOS) tab">
      Terminal (EMV/SoftPOS)
    </TextAction>
  ) : null;

  const readersLine = (
    <span data-testid="pt-readers-line" className="flex flex-col gap-0.5">
      {readers.length
        ? readers.map((r) => <span key={`${r.role}-${r.emvDeviceId}`}>{readerLineText(r)}</span>)
        : <span>No card reader yet — add a card reader on the Terminal (EMV/SoftPOS) tab</span>}
      {pending && readers.length > 0 && <span className="text-stone-500">Primary and backup readers are not set up yet — add them on the Terminal (EMV/SoftPOS) tab.</span>}
    </span>
  );

  const current = opts.find((o) => o.id === tipLocation);
  const tipLocationRow = (
    <SubRow
      testId="pt-tip-location"
      icon={<MapPin size={16} />}
      title="Tips location"
      desc={pending
        ? 'Saved with the card readers on the Terminal (EMV/SoftPOS) tab.'
        : tipLocation === 'off'
        ? 'Customers are not asked for a tip on card payments at this till.'
        : current?.disabled
          ? <span className="text-amber-700">{current.reason} — pick another place.</span>
          : tipLocation === 'customer_display'
            ? 'Asked once, on the customer display, before the card.'
            : 'Asked once, on the reader taking the card. If the payment moves to the other reader, the tip is already in the total.'}
      right={(
        <Segmented
          ariaLabel="Tips location"
          value={tipLocation}
          options={opts.map((o) => ({ id: o.id, label: o.label, disabled: !pending && o.disabled, title: pending ? undefined : o.reason }))}
          onChange={(id) => onTipLocation(id as TipLocation)}
          disabled={disabled}
        />
      )}
    />
  );

  return {
    readersLine,
    creditRight: <>{readers.length ? <Pill>Set on Terminal tab</Pill> : <Pill>No reader</Pill>}{link}</>,
    debit: { possible: locks.debitPossible, readerName: locks.debitReader ? readerName(locks.debitReader) : null, reason: locks.reason },
    tipLocationRow,
  };
}
